import { useCallback, useEffect, useState } from "react";
import { Search, X, XCircle, Eye } from "lucide-react";
import api, { inr, fmtDate, fmtErr } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import AdminShipmentDetail from "../../components/AdminShipmentDetail";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUSES = ["", "LABEL_GENERATED", "PICKUP_SCHEDULED", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED", "NDR", "RTO", "CANCELLED"];
const CANCELLABLE = ["ORDER_CREATED", "SHIPMENT_CREATED", "AWB_ASSIGNED", "LABEL_GENERATED", "PICKUP_SCHEDULED"];
const selectCls = "rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminShipments() {
  const [shipments, setShipments] = useState(null);
  const [providers, setProviders] = useState([]);
  const [filters, setFilters] = useState({ status: "", provider: "", payment_mode: "", awb: "", order_id: "" });
  const [trackFor, setTrackFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const { user } = useAuth();
  const canCancel = ["admin", "operations", "support"].includes(user?.role);

  useEffect(() => { api.get("/admin/providers").then(({ data }) => setProviders(data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/shipments", { params }).then(({ data }) => setShipments(data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const cancelShipment = async (s) => {
    if (!window.confirm(`Cancel shipment ${s.awb || s.order_number} (${s.courier_name})? The customer's wallet will be refunded ${inr(s.price)} after the courier confirms.`)) return;
    try {
      const { data } = await api.post(`/admin/shipments/${s.shipment_id}/cancel`);
      if (data.ok) {
        toast.success(`Cancelled ${s.awb} — refunded ${inr(data.refunded)}`);
      } else {
        toast.warning(data.message || "Courier rejected cancellation — pending retry", { duration: 6000 });
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-shipments-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">All Shipments</h1>
      <p className="text-sm text-slate-500">{shipments?.length ?? 0} shipments across all customers — with cost & margin per row.</p>

      <div className="mt-5 flex flex-wrap gap-3" data-testid="admin-shipment-filters">
        <select data-testid="afilter-status" className={selectCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All statuses"}</option>)}
        </select>
        <select data-testid="afilter-provider" className={selectCls} value={filters.provider} onChange={(e) => setFilters({ ...filters, provider: e.target.value })}>
          <option value="">All providers</option>
          {providers.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
        <select data-testid="afilter-payment" className={selectCls} value={filters.payment_mode} onChange={(e) => setFilters({ ...filters, payment_mode: e.target.value })}>
          <option value="">COD + Prepaid</option><option>Prepaid</option><option>COD</option>
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="afilter-awb" className={`${selectCls} mono w-44 pl-9`} placeholder="AWB" value={filters.awb} onChange={(e) => setFilters({ ...filters, awb: e.target.value })} />
        </div>
        <input data-testid="afilter-order" className={`${selectCls} mono w-44`} placeholder="Order ID" value={filters.order_id} onChange={(e) => setFilters({ ...filters, order_id: e.target.value })} />
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-shipments-table">
        <table className="w-full min-w-[1080px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Order / AWB</th><th className="px-4 py-3.5">Customer</th><th className="px-4 py-3.5">Courier</th>
              <th className="px-4 py-3.5">Route</th><th className="px-4 py-3.5">Wt</th><th className="px-4 py-3.5">Mode</th>
              <th className="px-4 py-3.5">Price</th><th className="px-4 py-3.5">Cost</th><th className="px-4 py-3.5">Margin</th>
              <th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5">Created</th><th className="px-4 py-3.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(shipments || []).map((s) => {
              const cost = s.pricing_breakdown?.provider_cost || 0;
              const profit = s.status !== "CANCELLED" ? s.price - cost : 0;
              return (
                <tr key={s.shipment_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-shipment-${s.awb}`}>
                  <td className="px-5 py-3"><button onClick={() => setDetailFor(s.shipment_id)} data-testid={`admin-order-detail-${s.shipment_id}`} className="mono text-left text-[10px] font-semibold text-slate-500 underline decoration-slate-300 underline-offset-2 transition-colors hover:text-shipany-blue">{s.order_number}</button><p className="mono text-xs font-semibold text-shipany-blue">{s.awb}</p></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{s.customer_email}{s.source === "api" && <span className="ml-1.5 rounded bg-cyan-100 px-1.5 py-0.5 text-[9px] font-bold text-cyan-700" data-testid={`api-badge-${s.awb}`}>API</span>}</td>
                  <td className="px-4 py-3 text-xs">{s.courier_name}<p className="capitalize text-[10px] text-slate-400">{s.provider_code}</p></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{s.origin_city} → {s.destination_city}</td>
                  <td className="mono px-4 py-3 text-xs">{s.weight}kg</td>
                  <td className="px-4 py-3 text-xs">{s.payment_mode}</td>
                  <td className="mono px-4 py-3 text-xs font-bold">{inr(s.price)}</td>
                  <td className="mono px-4 py-3 text-xs text-slate-500">{inr(cost)}</td>
                  <td className={`mono px-4 py-3 text-xs font-bold ${profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{inr(profit)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setTrackFor(s)} data-testid={`status-track-${s.awb}`} title="View tracking timeline"
                      className="rounded-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-shipany-blue/40">
                      <StatusBadge status={s.status} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(s.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setDetailFor(s.shipment_id)} data-testid={`view-${s.awb || s.shipment_id}`} title="Full shipment detail"
                        className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-200">
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                      {s.cancellation_pending ? (
                        <span className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-[10px] font-bold text-amber-600" data-testid={`cancel-pending-${s.awb}`}>Cancelling…</span>
                      ) : canCancel && s.source !== "api" && !["CANCELLED", "DELIVERED", "RTO", "RTO_DELIVERED", "RETURNED"].includes(s.status) ? (
                        <button onClick={() => cancelShipment(s)} data-testid={`cancel-${s.awb}`}
                          title={CANCELLABLE.includes(s.status) ? "Cancel shipment (courier confirms)" : "Try cancel — courier live status decides"}
                          className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 transition-colors hover:bg-red-100">
                          <XCircle className="h-3.5 w-3.5" /> Cancel
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {shipments && shipments.length === 0 && <tr><td colSpan={12} className="px-6 py-12 text-center text-slate-400">No shipments match.</td></tr>}
          </tbody>
        </table>
      </div>

      {detailFor && <AdminShipmentDetail shipmentId={detailFor} onClose={() => setDetailFor(null)} />}

      {trackFor && (() => {
        const events = [...(trackFor.tracking_events || [])].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-shipany-navy/40 p-4" onClick={() => setTrackFor(null)} data-testid="tracking-modal-overlay">
            <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="tracking-modal">
              <div className="flex items-start justify-between">
                <div>
                  <p className="mono text-[10px] text-slate-400">{trackFor.order_number || trackFor.order_id}</p>
                  <h2 className="mono text-lg font-bold text-shipany-navy" data-testid="tracking-modal-awb">{trackFor.awb || "AWB pending"}</h2>
                  <p className="text-xs text-slate-500">{trackFor.courier_name} · {trackFor.origin_city} → {trackFor.destination_city}{trackFor.source === "api" && <span className="ml-1.5 rounded bg-cyan-100 px-1.5 py-0.5 text-[9px] font-bold text-cyan-700">API</span>}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={trackFor.status} />
                  <button onClick={() => setTrackFor(null)} data-testid="tracking-modal-close" className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><X className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-5" data-testid="tracking-timeline">
                {events.length === 0 && (
                  <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-xs text-slate-400" data-testid="tracking-empty">
                    No courier scans yet — live tracking syncs every 15 minutes once the courier picks up.
                  </p>
                )}
                <ol className="relative space-y-4 border-l-2 border-slate-100 pl-5">
                  {events.map((e, i) => (
                    <li key={i} className="relative" data-testid={`tracking-event-${i}`}>
                      <span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white ${i === 0 ? "bg-shipany-blue" : "bg-slate-300"}`} />
                      <div className="flex items-center gap-2">
                        <StatusBadge status={e.status} />
                        <span className="text-[10px] text-slate-400">{fmtDate(e.timestamp)}</span>
                      </div>
                      {e.note && <p className="mt-1 text-xs text-slate-600">{e.note}</p>}
                      {e.location && <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{e.location}</p>}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
